/* Practice partners inquiry. sections/partners.liquid renders the form; this element validates
   it, formats the phone, reveals "Tell us more" for Other, and POSTs one JSON body to Backbone
   through the shared quiz-form.js helpers. The client token survives retries and reloads in this
   tab and is cleared on success, so a "did it go through?" resubmit can never file a duplicate.
   Nothing typed reaches analytics. */
(() => {
  const shared = window.MosesQuizForm;
  if (!shared) return;
  const { request, formatPhone, emailValid, phoneValid, track, transition, MESSAGES } = shared;
  const KEY = "moses_partners_v1";
  const OTHER = "Other"; // The option sections/partners.liquid always appends last.
  const PATH = "/v1/partner-inquiries";
  const FIELDS = ["name", "practice_name", "email", "phone", "location", "referral_source", "referral_detail", "message", "website"];
  const COPY = {
    email: "Please enter a valid email address.",
    phone: "Please enter a valid phone number.",
    incomplete: "Please fill in every field.",
  };

  const readToken = () => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) || "{}").clientToken || "";
    } catch {
      return "";
    }
  };
  const saveToken = (clientToken) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ clientToken }));
    } catch {
      /* in-memory fallback for this page view */
    }
  };
  const clearToken = () => {
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      /* nothing to clear */
    }
  };
  const mintToken = () =>
    globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const isOther = (values) => values.referral_source === OTHER;

  class MosesPartners extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const $ = (selector) => this.querySelector(selector);
      this.form = $("form");
      this.formView = $("[data-partners-form]");
      this.successView = $("[data-partners-success]");
      this.otherRow = $("[data-partners-other]");
      this.submitButton = $("[data-partners-submit]");
      this.errorEl = $("[data-partners-error]");
      this.inputs = Object.fromEntries(FIELDS.map((name) => [name, $(`[name="${name}"]`)]));
      this.memoryToken = "";
      this.submitting = false;
      this.onInput = () => this.update();
      this.onSubmit = (event) => {
        event.preventDefault();
        void this.submit();
      };
      this.form.addEventListener("input", this.onInput);
      this.form.addEventListener("change", this.onInput);
      this.form.addEventListener("submit", this.onSubmit);
      this.dataset.state = "form";
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.form?.removeEventListener("input", this.onInput);
      this.form?.removeEventListener("change", this.onInput);
      this.form?.removeEventListener("submit", this.onSubmit);
    }
    values() {
      return Object.fromEntries(FIELDS.map((name) => [name, (this.inputs[name]?.value || "").trim()]));
    }
    valid(values) {
      return (
        Boolean(values.name && values.practice_name && values.location && values.referral_source) &&
        emailValid(values.email) &&
        phoneValid(values.phone) &&
        (!isOther(values) || Boolean(values.referral_detail))
      );
    }
    update() {
      const phone = this.inputs.phone;
      const formatted = formatPhone(phone.value);
      if (formatted !== phone.value) phone.value = formatted;
      const values = this.values();
      const other = isOther(values);
      this.otherRow.hidden = !other;
      this.inputs.referral_detail.required = other;
      if (!other) this.inputs.referral_detail.value = "";
      this.error("");
      this.submitButton.disabled = this.submitting || !this.valid(values);
    }
    error(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
      if (message) this.errorEl.focus();
    }
    clientToken() {
      let token = readToken() || this.memoryToken;
      if (!token) {
        token = mintToken();
        this.memoryToken = token;
        saveToken(token);
      }
      return token;
    }
    body(values) {
      return {
        name: values.name,
        practice_name: values.practice_name,
        email: values.email,
        phone: values.phone,
        location: values.location,
        referral_source: isOther(values) ? `${OTHER}: ${values.referral_detail}` : values.referral_source,
        message: values.message || undefined, // Their remarks, optional; absent keeps the dedupe key short.
        source: this.dataset.source || "moses_partners_page",
        client_token: this.clientToken(),
        website: values.website, // honeypot: "" for a person
        ...shared.metaContext?.(this.clientToken()),
      };
    }
    async submit() {
      if (this.submitting) return;
      const values = this.values();
      if (!emailValid(values.email)) return this.error(COPY.email);
      if (!phoneValid(values.phone)) return this.error(COPY.phone);
      if (!this.valid(values)) return this.error(COPY.incomplete);
      this.submitting = true;
      this.submitButton.disabled = true;
      this.submitButton.textContent = "Submitting";
      this.error("");
      try {
        const result = await request(this.dataset.apiBase || "", PATH, { method: "POST", body: this.body(values), signal: this.controller.signal });
        const eventId = result.meta_event_id || this.clientToken();
        clearToken();
        this.memoryToken = "";
        track("partner_inquiry_submitted", { event_id: eventId });
        await this.renderSuccess();
      } catch (error) {
        if (error.name === "AbortError") return;
        this.error(error.message || MESSAGES.generic);
        this.submitButton.textContent = "Try again";
        this.submitButton.disabled = false;
      } finally {
        this.submitting = false;
      }
    }
    renderSuccess() {
      return transition(this, "forward", () => {
        this.dataset.state = "success";
        this.formView.hidden = true;
        this.successView.hidden = false;
        window.scrollTo?.(0, 0);
        this.successView.querySelector("h1")?.focus();
      });
    }
  }

  if (!customElements.get("moses-partners")) customElements.define("moses-partners", MosesPartners);
})();
